/**
 * Offline-first pending runs: small CSV imports use inline runData in localStorage;
 * large / RideView saves use IndexedDB (localforage) + queue metadata in localStorage.
 */

import localforage from 'localforage';
import { collection, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { saveRunTelemetrySnapshot, loadRunTelemetrySnapshot } from './runTelemetrySnapshot';

export const OFFLINE_RUN_QUEUE_KEY = 'pendingHomeCsvRuns_v1';
const FIRESTORE_DOC_SAFE_BYTES = 900_000;

/** Same thresholds as RideView save: offload to Storage before Firestore when exceeded. */
export const TELEMETRY_OFFLOAD_JSON_LEN = 700_000;
export const TELEMETRY_OFFLOAD_ROW_COUNT = 15_000;
export const STORAGE_OFFLOAD_TIMEOUT_MS = 120_000;

/**
 * True only for plausible http(s) URLs (rejects literal "undefined"/"null" and other bad doc values).
 * Firebase Storage download URLs are always https.
 */
export function isValidTelemetryDataDownloadUrl(url) {
  if (typeof url !== 'string') return false;
  const t = url.trim();
  if (!t || t === 'undefined' || t === 'null') return false;
  if (!/^https?:\/\//i.test(t)) return false;
  try {
    const u = new URL(t);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

/** Sort key millis for merging cloud + pending run lists (Home, My Rides). */
export function pendingRunSortMillis(v) {
  if (v == null) return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return v < 1e12 ? v * 1000 : v;
  if (typeof v === 'string') {
    const ms = Date.parse(v);
    return Number.isFinite(ms) ? ms : 0;
  }
  if (typeof v === 'object') {
    if (typeof v.toMillis === 'function') {
      const ms = v.toMillis();
      return typeof ms === 'number' && Number.isFinite(ms) ? ms : 0;
    }
    if (typeof v.toDate === 'function') {
      const d = v.toDate();
      const ms = d?.getTime?.();
      return typeof ms === 'number' && Number.isFinite(ms) ? ms : 0;
    }
    if (typeof v.seconds === 'number' && Number.isFinite(v.seconds)) return v.seconds * 1000;
  }
  return 0;
}

/** Dedupe by id, pending wins on collision; sort newest first. */
export function mergeFirestoreRunsWithPending(firestoreRuns, pendingRuns) {
  const map = new Map();
  for (const r of firestoreRuns || []) {
    if (r?.id) map.set(r.id, r);
  }
  for (const p of pendingRuns || []) {
    if (p?.id) map.set(p.id, p);
  }
  const combined = Array.from(map.values());
  const sortKey = (r) => pendingRunSortMillis(r.timestamp || r.date || r.createdAt);
  return combined.sort((a, b) => sortKey(b) - sortKey(a));
}

const draftStore = localforage.createInstance({
  name: 'shokiq-pwa',
  storeName: 'pendingRunDrafts',
});

function isFirestoreTimestamp(v) {
  return v && typeof v === 'object' && typeof v.toMillis === 'function';
}

/**
 * Clone run payload for storage (Firestore Timestamps → plain markers).
 */
export function serializeRunForOfflineDraft(obj, seen = new WeakSet()) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (seen.has(obj)) return null;
  if (isFirestoreTimestamp(obj)) {
    return { __firestoreTsMs: obj.toMillis() };
  }
  if (obj instanceof Date) {
    return { __dateMs: obj.getTime() };
  }
  if (Array.isArray(obj)) {
    seen.add(obj);
    const out = obj.map((item) => serializeRunForOfflineDraft(item, seen));
    seen.delete(obj);
    return out;
  }
  seen.add(obj);
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = serializeRunForOfflineDraft(v, seen);
  }
  seen.delete(obj);
  return out;
}

/**
 * Restore payload for Firestore writes.
 */
export function deserializeRunFromOfflineDraft(obj, seen = new WeakSet()) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (seen.has(obj)) return obj;
  if (typeof obj.__firestoreTsMs === 'number') {
    return Timestamp.fromMillis(obj.__firestoreTsMs);
  }
  if (typeof obj.__dateMs === 'number') {
    return new Date(obj.__dateMs);
  }
  if (
    typeof obj.seconds === 'number' &&
    typeof obj.nanoseconds === 'number' &&
    Object.keys(obj).length <= 3
  ) {
    return new Timestamp(obj.seconds, obj.nanoseconds);
  }
  if (Array.isArray(obj)) {
    seen.add(obj);
    const out = obj.map((item) => deserializeRunFromOfflineDraft(item, seen));
    seen.delete(obj);
    return out;
  }
  seen.add(obj);
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = deserializeRunFromOfflineDraft(v, seen);
  }
  seen.delete(obj);
  return out;
}

export function createLocalDraftId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export async function enqueuePendingRunDraft(serializedRunData) {
  const localDraftId = createLocalDraftId();
  await draftStore.setItem(localDraftId, serializedRunData);
  let queue = [];
  try {
    const raw = localStorage.getItem(OFFLINE_RUN_QUEUE_KEY);
    queue = raw ? JSON.parse(raw) : [];
  } catch {
    queue = [];
  }
  if (!Array.isArray(queue)) queue = [];
  queue.push({ localDraftId, createdLocallyAt: Date.now() });
  localStorage.setItem(OFFLINE_RUN_QUEUE_KEY, JSON.stringify(queue));
  return localDraftId;
}

/** Legacy path: entire run in localStorage (small payloads). */
export function enqueuePendingRunInline(runData) {
  const clientKey = `pe_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  let queue = [];
  try {
    const raw = localStorage.getItem(OFFLINE_RUN_QUEUE_KEY);
    queue = raw ? JSON.parse(raw) : [];
  } catch {
    queue = [];
  }
  if (!Array.isArray(queue)) queue = [];
  queue.push({ runData, createdLocallyAt: Date.now(), clientKey });
  localStorage.setItem(OFFLINE_RUN_QUEUE_KEY, JSON.stringify(queue));
  return clientKey;
}

/** Load inline queued run payload for RideView / diagnostics (clientKey from `queued-inline-${key}`). */
export function getPendingInlineRunByClientKey(clientKey) {
  if (!clientKey) return null;
  let queue = [];
  try {
    const raw = localStorage.getItem(OFFLINE_RUN_QUEUE_KEY);
    queue = raw ? JSON.parse(raw) : [];
  } catch {
    return null;
  }
  if (!Array.isArray(queue)) return null;
  const entry = queue.find((e) => e && e.clientKey === clientKey && e.runData);
  if (!entry?.runData) return null;
  return { runData: entry.runData, createdLocallyAt: entry.createdLocallyAt };
}

/** Remove a small-payload queue row (e.g. delete from My Rides). */
export function removePendingQueueEntryByClientKey(clientKey) {
  if (!clientKey) return false;
  let queue = [];
  try {
    const raw = localStorage.getItem(OFFLINE_RUN_QUEUE_KEY);
    queue = raw ? JSON.parse(raw) : [];
  } catch {
    // Corrupt or unreadable queue — treat as nothing left to delete for UX/idempotency.
    return true;
  }
  if (!Array.isArray(queue)) return true;
  const next = queue.filter((e) => e.clientKey !== clientKey);
  if (next.length === queue.length) return true;
  try {
    localStorage.setItem(OFFLINE_RUN_QUEUE_KEY, JSON.stringify(next));
  } catch (err) {
    console.warn('removePendingQueueEntryByClientKey persist failed:', err);
  }
  return true;
}

/** Update fields on an inline queued run payload by clientKey. */
export function updatePendingInlineRunByClientKey(clientKey, partialRunData) {
  if (!clientKey || !partialRunData || typeof partialRunData !== 'object') return false;
  let queue = [];
  try {
    const raw = localStorage.getItem(OFFLINE_RUN_QUEUE_KEY);
    queue = raw ? JSON.parse(raw) : [];
  } catch {
    return false;
  }
  if (!Array.isArray(queue)) return false;
  const idx = queue.findIndex((e) => e && e.clientKey === clientKey && e.runData && typeof e.runData === 'object');
  if (idx < 0) return false;
  queue[idx] = {
    ...queue[idx],
    runData: {
      ...queue[idx].runData,
      ...partialRunData,
    },
  };
  try {
    localStorage.setItem(OFFLINE_RUN_QUEUE_KEY, JSON.stringify(queue));
    return true;
  } catch (err) {
    console.warn('updatePendingInlineRunByClientKey persist failed:', err);
    return false;
  }
}

function timestampMillisFromSerializedDraft(draft) {
  if (!draft || typeof draft !== 'object') return 0;
  const t = draft.timestamp;
  if (typeof t === 'number') return t < 1e12 ? t * 1000 : t;
  if (t && typeof t === 'object') {
    if (typeof t.__firestoreTsMs === 'number') return t.__firestoreTsMs;
    if (typeof t.seconds === 'number') return t.seconds * 1000;
  }
  const d = draft.date;
  if (d && typeof d === 'object' && typeof d.__firestoreTsMs === 'number') return d.__firestoreTsMs;
  if (d && typeof d === 'object' && typeof d.seconds === 'number') return d.seconds * 1000;
  return 0;
}

function inlineRunTimestampMillis(runData, createdLocallyAt) {
  if (!runData || typeof runData !== 'object') return createdLocallyAt;
  const t = runData.timestamp;
  if (t && typeof t.toMillis === 'function') {
    const ms = t.toMillis();
    return typeof ms === 'number' && Number.isFinite(ms) ? ms : createdLocallyAt;
  }
  if (typeof t === 'number') return t < 1e12 ? t * 1000 : t;
  if (t && typeof t.seconds === 'number') return t.seconds * 1000;
  return createdLocallyAt;
}

/**
 * Build lightweight run-shaped objects for My Rides (current user only).
 */
export async function listPendingRunsForDisplay(userId) {
  if (!userId) return [];
  let queue = [];
  try {
    const raw = localStorage.getItem(OFFLINE_RUN_QUEUE_KEY);
    queue = raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
  if (!Array.isArray(queue)) return [];

  let mutated = false;
  for (let i = 0; i < queue.length; i++) {
    const e = queue[i];
    if (e?.runData && !e.localDraftId && !e.clientKey) {
      queue[i] = {
        ...e,
        clientKey: `mig_${e.createdLocallyAt ?? Date.now()}_${i}_${Math.random().toString(36).slice(2, 9)}`,
      };
      mutated = true;
    }
  }
  if (mutated) {
    try {
      localStorage.setItem(OFFLINE_RUN_QUEUE_KEY, JSON.stringify(queue));
    } catch (err) {
      console.warn('Could not persist queue clientKey migration:', err);
    }
  }

  const out = [];
  for (let i = 0; i < queue.length; i++) {
    const entry = queue[i];
    if (entry.localDraftId) {
      const draft = await getPendingRunDraft(entry.localDraftId);
      if (!draft || draft.userId !== userId) continue;
      const name = draft.runName || draft.name || 'Unnamed Run';
      const tsMs = timestampMillisFromSerializedDraft(draft) || entry.createdLocallyAt;
      const telLen = Array.isArray(draft.telemetryData) ? draft.telemetryData.length : 0;
      out.push({
        id: `local-${entry.localDraftId}`,
        userId,
        runName: name,
        name: name,
        description: typeof draft.description === 'string' ? draft.description : '',
        videoFileName: draft.videoFileName || null,
        videoUrl: null,
        youtubeUrl: null,
        timestamp: tsMs,
        createdAt: entry.createdLocallyAt,
        date: tsMs ? { seconds: Math.floor(tsMs / 1000) } : undefined,
        telemetryData: [],
        pendingSync: true,
        pendingTelemetryCount: telLen,
        hasTelemetryData: telLen > 0,
      });
    } else if (entry.runData && entry.runData.userId === userId) {
      const rd = entry.runData;
      const name = rd.runName || rd.name || 'Unnamed Run';
      const createdLocallyAt = entry.createdLocallyAt || Date.now();
      const tsMs = inlineRunTimestampMillis(rd, createdLocallyAt);
      const telLen = Array.isArray(rd.telemetryData) ? rd.telemetryData.length : 0;
      const clientKey = entry.clientKey;
      if (!clientKey) continue;
      out.push({
        id: `queued-inline-${clientKey}`,
        userId,
        runName: name,
        name: name,
        description: typeof rd.description === 'string' ? rd.description : '',
        videoFileName: rd.videoFileName || null,
        videoUrl: rd.videoUrl || null,
        youtubeUrl: rd.youtubeUrl || null,
        timestamp: tsMs,
        createdAt: createdLocallyAt,
        date: rd.date,
        telemetryData: [],
        pendingSync: true,
        pendingTelemetryCount: telLen,
        pendingClientKey: clientKey,
        hasTelemetryData: telLen > 0,
      });
    }
  }
  return out;
}

export async function getPendingRunDraft(localDraftId) {
  return draftStore.getItem(localDraftId);
}

export async function removePendingRunDraft(localDraftId) {
  await draftStore.removeItem(localDraftId);
}

function removeQueueEntryForDraft(localDraftId) {
  let queue = [];
  try {
    const raw = localStorage.getItem(OFFLINE_RUN_QUEUE_KEY);
    queue = raw ? JSON.parse(raw) : [];
  } catch {
    queue = [];
  }
  if (!Array.isArray(queue)) queue = [];
  const next = queue.filter((e) => e.localDraftId !== localDraftId);
  localStorage.setItem(OFFLINE_RUN_QUEUE_KEY, JSON.stringify(next));
}

export async function discardLocalPendingRun(localDraftId) {
  if (!localDraftId) return;
  // Drop the queue row first so listPendingRuns / merge no longer sees this draft even if IndexedDB fails.
  removeQueueEntryForDraft(localDraftId);
  try {
    await removePendingRunDraft(localDraftId);
  } catch (err) {
    console.warn('removePendingRunDraft failed:', err);
  }
}

/** Shallow merge stored draft with serialized partial fields. */
export async function updatePendingRunDraft(localDraftId, partialSerialized) {
  const prev = await getPendingRunDraft(localDraftId);
  if (!prev) return false;
  await draftStore.setItem(localDraftId, { ...prev, ...partialSerialized });
  return true;
}

/**
 * Upload large inline telemetry to Storage (same path convention as RideView).
 * On failure (e.g. 403 rules), returns `data` unchanged so the draft stays fully local until rules/sync succeed.
 */
export async function tryOffloadRunTelemetryToStorage(user, pathKeySegment, data) {
  if (!user?.uid || !data || typeof data !== 'object') return data;
  const work = { ...data };
  if (work.telemetryDataUrl != null && !isValidTelemetryDataDownloadUrl(work.telemetryDataUrl)) {
    delete work.telemetryDataUrl;
  }
  const tel = work.telemetryData;
  if (!Array.isArray(tel) || tel.length === 0) return work;
  if (isValidTelemetryDataDownloadUrl(work.telemetryDataUrl)) return work;

  let json;
  try {
    json = JSON.stringify(tel);
  } catch {
    return work;
  }
  const needsOffload =
    json.length > TELEMETRY_OFFLOAD_JSON_LEN || tel.length > TELEMETRY_OFFLOAD_ROW_COUNT;
  if (!needsOffload) return work;

  const safeSeg = String(pathKeySegment || 'run').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 120);
  try {
    const telemetryRef = ref(storage, `runs/${user.uid}/${safeSeg}/telemetry.json`);
    const body = new Blob([json], { type: 'application/json' });
    await Promise.race([
      uploadBytes(telemetryRef, body, { contentType: 'application/json' }),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('storage-timeout')), STORAGE_OFFLOAD_TIMEOUT_MS);
      }),
    ]);
    const telemetryDataUrl = await getDownloadURL(telemetryRef);
    const probe = await Promise.race([
      fetch(telemetryDataUrl),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('telemetry-url-probe-timeout')), 45_000);
      }),
    ]);
    const probeText = await probe.text();
    const probeTrim = typeof probeText === 'string' ? probeText.trim() : '';
    if (!probe.ok || !probeTrim || probeTrim === 'undefined' || probeTrim === 'null') {
      throw new Error('telemetry-upload-probe-empty');
    }
    const parsed = JSON.parse(probeTrim);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      throw new Error('telemetry-upload-probe-not-array');
    }
    return { ...work, telemetryData: [], telemetryDataUrl };
  } catch (e) {
    console.warn('tryOffloadRunTelemetryToStorage failed:', e);
    return work;
  }
}

function buildFirestoreRunCreatePayload(user, data) {
  const payload = {
    ...data,
    userId: user.uid,
    ownerId: user.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (payload.telemetryDataUrl != null && !isValidTelemetryDataDownloadUrl(payload.telemetryDataUrl)) {
    delete payload.telemetryDataUrl;
  }
  const telemetryCount = Array.isArray(payload.telemetryData) ? payload.telemetryData.length : 0;
  const hasUrl = isValidTelemetryDataDownloadUrl(payload.telemetryDataUrl);
  let tooLargeForFirestore = false;
  try {
    const { createdAt: _c, updatedAt: _u, ...rest } = payload;
    const measure = JSON.stringify(rest);
    if (typeof measure === 'string' && measure.length > FIRESTORE_DOC_SAFE_BYTES) {
      tooLargeForFirestore = telemetryCount > 0 && !hasUrl;
    }
  } catch {
    tooLargeForFirestore = telemetryCount > 0 && !hasUrl;
  }
  return { payload, tooLargeForFirestore };
}

/**
 * Create a Firestore run doc from offline-serialized data.
 * @returns {Promise<{ id: string }>}
 * @throws Error with code `RUN_TELEMETRY_TOO_LARGE_FOR_FIRESTORE` if telemetry cannot fit and is not URL-backed.
 */
export async function addDocFromSerializedRun(db, user, serializedRunData) {
  const data = deserializeRunFromOfflineDraft(serializedRunData);
  const { payload, tooLargeForFirestore } = buildFirestoreRunCreatePayload(user, data);
  if (tooLargeForFirestore) {
    const err = new Error('RUN_TELEMETRY_TOO_LARGE_FOR_FIRESTORE');
    err.code = 'RUN_TELEMETRY_TOO_LARGE_FOR_FIRESTORE';
    throw err;
  }
  const docRef = await addDoc(collection(db, 'runs'), payload);
  return { id: docRef.id };
}

/** Prevents concurrent Firestore writes for the same draft (e.g. flush + RideView). */
const syncingDraftIds = new Set();

/**
 * Write one IndexedDB draft to Firestore; remove draft + queue row only after success.
 * Returns new document id, or null on failure / missing draft / already syncing.
 */
export async function tryUploadPendingDraftOnce(db, user, localDraftId) {
  if (!user || !navigator.onLine) return null;
  if (syncingDraftIds.has(localDraftId)) return null;
  const serialized = await getPendingRunDraft(localDraftId);
  if (!serialized) return null;

  syncingDraftIds.add(localDraftId);
  try {
    const runData = deserializeRunFromOfflineDraft(serialized);
    // Snapshot must be taken before offload replaces inline telemetry with [] in `prepared`.
    const snapshot = Array.isArray(runData.telemetryData) ? runData.telemetryData : [];
    const prepared = await tryOffloadRunTelemetryToStorage(user, `draft-${localDraftId}`, runData);
    const serializedPrepared = serializeRunForOfflineDraft(prepared);
    const { id } = await addDocFromSerializedRun(db, user, serializedPrepared);
    if (snapshot.length > 0) {
      try {
        await saveRunTelemetrySnapshot(user.uid, id, snapshot);
        const verify = await loadRunTelemetrySnapshot(user.uid, id);
        if (!Array.isArray(verify) || verify.length !== snapshot.length) {
          console.error('Telemetry IndexedDB snapshot length mismatch after save', {
            expected: snapshot.length,
            got: verify?.length ?? 0,
          });
        }
      } catch (snapErr) {
        console.error('saveRunTelemetrySnapshot failed:', snapErr);
      }
    }
    await removePendingRunDraft(localDraftId);
    removeQueueEntryForDraft(localDraftId);
    return id;
  } catch (err) {
    if (err?.code === 'RUN_TELEMETRY_TOO_LARGE_FOR_FIRESTORE') {
      console.warn(
        'tryUploadPendingDraftOnce: telemetry too large for Firestore and Storage offload failed; keeping local draft for retry.',
      );
    } else {
      console.warn('tryUploadPendingDraftOnce failed:', err);
    }
    return null;
  } finally {
    syncingDraftIds.delete(localDraftId);
  }
}

async function syncOneQueueEntry(db, user, entry) {
  if (entry.localDraftId) {
    const serialized = await getPendingRunDraft(entry.localDraftId);
    if (!serialized) {
      return { ok: true, firestoreId: null, orphan: true };
    }
    const runForUi = deserializeRunFromOfflineDraft(serialized);
    const id = await tryUploadPendingDraftOnce(db, user, entry.localDraftId);
    if (id) return { ok: true, firestoreId: id, runForUi };
    return { ok: false, firestoreId: null };
  }
  let serialized = entry.runData;
  if (!serialized) {
    return { ok: false, firestoreId: null };
  }
  let data = deserializeRunFromOfflineDraft(serialized);
  const pathKey =
    (entry.clientKey && String(entry.clientKey).replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 120)) ||
    `q-${entry.createdLocallyAt ?? Date.now()}`;
  data = await tryOffloadRunTelemetryToStorage(user, pathKey, data);
  const preparedSerialized = serializeRunForOfflineDraft(data);
  try {
    const { id } = await addDocFromSerializedRun(db, user, preparedSerialized);
    const runForUi = deserializeRunFromOfflineDraft(preparedSerialized);
    return { ok: true, firestoreId: id, runForUi };
  } catch (err) {
    if (err?.code === 'RUN_TELEMETRY_TOO_LARGE_FOR_FIRESTORE') {
      console.warn('Inline pending run still too large after Storage offload; will retry later.', err);
      return { ok: false, firestoreId: null };
    }
    throw err;
  }
}

/**
 * Sync all queued runs (online). Processes one entry at a time and removes it from the
 * queue before attempting upload to avoid duplicate docs if multiple tabs flush.
 */
export async function flushPendingRuns(db, user, options = {}) {
  const onSynced = typeof options?.onSynced === 'function' ? options.onSynced : null;
  if (!user || !navigator.onLine) return 0;
  let synced = 0;
  while (true) {
    let queue = [];
    try {
      const raw = localStorage.getItem(OFFLINE_RUN_QUEUE_KEY);
      queue = raw ? JSON.parse(raw) : [];
    } catch {
      break;
    }
    if (!Array.isArray(queue) || queue.length === 0) break;

    const [entry, ...rest] = queue;
    localStorage.setItem(OFFLINE_RUN_QUEUE_KEY, JSON.stringify(rest));

    try {
      const result = await syncOneQueueEntry(db, user, entry);
      if (result.orphan) {
        continue;
      }
      if (result.ok && result.firestoreId) {
        synced += 1;
        if (onSynced) {
          try {
            onSynced({ entry, firestoreId: result.firestoreId, runForUi: result.runForUi || null });
          } catch (callbackErr) {
            console.warn('flushPendingRuns onSynced callback failed:', callbackErr);
          }
        }
      } else {
        const rawCurrent = localStorage.getItem(OFFLINE_RUN_QUEUE_KEY);
        let current = [];
        try {
          current = rawCurrent ? JSON.parse(rawCurrent) : [];
        } catch {
          current = [];
        }
        if (!Array.isArray(current)) current = [];
        localStorage.setItem(OFFLINE_RUN_QUEUE_KEY, JSON.stringify([entry, ...current]));
      }
    } catch (err) {
      console.error('Pending run sync failed:', err);
      const rawCurrent = localStorage.getItem(OFFLINE_RUN_QUEUE_KEY);
      let current = [];
      try {
        current = rawCurrent ? JSON.parse(rawCurrent) : [];
      } catch {
        current = [];
      }
      if (!Array.isArray(current)) current = [];
      localStorage.setItem(OFFLINE_RUN_QUEUE_KEY, JSON.stringify([entry, ...current]));
    }
  }
  return synced;
}

/**
 * Sync a single IndexedDB draft (e.g. while viewing /ride/local-*).
 * Queue row is removed only after a successful Firestore write.
 */
export async function syncPendingDraftById(db, user, localDraftId) {
  if (!user || !navigator.onLine) return null;
  const serialized = await getPendingRunDraft(localDraftId);
  if (!serialized) return null;
  return tryUploadPendingDraftOnce(db, user, localDraftId);
}
